import { HOST_TOOL_NAMES } from "../../turns/turn-constants";
import type { ToolActivity } from "../tool-activity";
import type { ToolActivityState } from "../tool-activity-state";
import { browserLabel } from "./browser-label";
import { toolInput } from "../tool-input";
import { describeBrowserAct } from "./describe-browser-act";
import { describeBrowserNavigate } from "./describe-browser-navigate";
import { describeTakeScreenshot } from "../capture/describe-take-screenshot";

/** The browser + REPL half of the canonical describers, kept out of the big
 * map so neither file outgrows the line cap. */
export const browserToolActivityDescribers: readonly [
  string,
  (record: Record<string, unknown>, state: ToolActivityState) => ToolActivity,
][] = [
  [HOST_TOOL_NAMES.BROWSER_NAVIGATE, describeBrowserNavigate],
  [HOST_TOOL_NAMES.BROWSER_ACT, describeBrowserAct],
  [
    HOST_TOOL_NAMES.BROWSER_SNAPSHOT,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not read the page",
        running: "Reading the page",
        done: "Read the page",
      }),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_WAIT,
    (_record, state) =>
      browserLabel(state, {
        failed: "Gave up waiting for the page",
        running: "Waiting for the page",
        done: "Waited for the page",
      }),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_INSPECT,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not read the console and network",
        running: "Reading the console and network",
        done: "Read the console and network",
      }),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_SCREENSHOT,
    (record, state) => describeTakeScreenshot(record, state),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_RECORD_START,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not start recording",
        running: "Starting a recording",
        done: "Started recording the browser",
      }),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_RECORD_STOP,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not finish the recording",
        running: "Finishing the recording",
        done: "Recorded the browser",
      }),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_CLOSE,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not close the browser",
        running: "Closing the browser",
        done: "Closed the browser",
      }),
  ],
  [
    HOST_TOOL_NAMES.BROWSER_SESSIONS_LIST,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not read your captured logins",
        running: "Listing your captured logins",
        done: "Listed your captured logins",
      }),
  ],
  [
    // The site, never what the vault holds for it.
    HOST_TOOL_NAMES.BROWSER_USE_SESSION,
    (record, state) => {
      const domain = toolInput.str(record.domain);
      return browserLabel(state, {
        failed: domain
          ? `Could not sign in to ${domain} as you`
          : "Could not load a captured login",
        running: domain
          ? `Signing in to ${domain} as you`
          : "Loading a captured login",
        done: domain
          ? `Signed in to ${domain} as you`
          : "Loaded a captured login",
      });
    },
  ],
  [
    HOST_TOOL_NAMES.BROWSER_FORGET_SESSION,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not forget the captured logins",
        running: "Forgetting the captured logins",
        done: "Forgot the captured logins",
      }),
  ],
  [
    HOST_TOOL_NAMES.REPL_EVAL,
    (_record, state) =>
      browserLabel(state, {
        failed: "JavaScript in the REPL failed",
        running: "Running JavaScript in the REPL",
        done: "Ran JavaScript in the REPL",
      }),
  ],
  [
    HOST_TOOL_NAMES.REPL_RESET,
    (_record, state) =>
      browserLabel(state, {
        failed: "Could not reset the REPL",
        running: "Resetting the REPL",
        done: "Reset the REPL",
      }),
  ],
];
